import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AssistantUrlService {
  private assistantIdSubject = new BehaviorSubject<string>('');
  public assistantId$ = this.assistantIdSubject.asObservable();

  constructor(private router: Router) {}

  /**
   * 從當前 URL 參數中取得 assistantid
   * @returns assistantid 的值，如果沒有則返回空字串
   */
  getAssistantIdFromUrl(): string {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const assistantId = urlParams.get('id') || '';
      
      // 更新 BehaviorSubject
      this.assistantIdSubject.next(assistantId);
      
      console.log('從 URL 取得的 assistantid:', assistantId);
      return assistantId;
    } catch (error) {
      console.error('取得 assistantid 時發生錯誤:', error);
      return '';
    }
  }

  /**
   * 從指定的 ActivatedRoute 中取得 assistantid
   * @param route ActivatedRoute 實例
   * @returns assistantid 的值，如果沒有則返回空字串
   */
  getAssistantIdFromRoute(route: ActivatedRoute): string {
    try {
      let assistantId = '';
      
      // 嘗試從當前路由的查詢參數中取得
      route.queryParams.subscribe(params => {
        assistantId = params['id'] || '';
      });

      // 如果當前路由沒有，嘗試從父路由中取得
      if (!assistantId && route.parent) {
        route.parent.queryParams.subscribe(params => {
          assistantId = params['id'] || '';
        });
      }

      // 如果還是沒有，嘗試從根路由取得
      if (!assistantId) {
        assistantId = this.getAssistantIdFromUrl();
      }

      // 更新 BehaviorSubject
      this.assistantIdSubject.next(assistantId);
      
      console.log('從路由取得的 assistantid:', assistantId);
      return assistantId;
    } catch (error) {
      console.error('從路由取得 assistantid 時發生錯誤:', error);
      return '';
    }
  }

  /**
   * 取得當前的 assistantid（同步方法）
   * @returns 當前的 assistantid 值
   */
  getCurrentAssistantId(): string {
    return this.assistantIdSubject.value || this.getAssistantIdFromUrl();
  }

  /**
   * 手動設定 assistantid（適用於特殊情況）
   * @param assistantId 要設定的 assistantid
   */
  setAssistantId(assistantId: string): void {
    this.assistantIdSubject.next(assistantId);
    console.log('手動設定 assistantid:', assistantId);
  }

  /**
   * 訂閱 assistantid 的變化
   * @returns Observable<string>
   */
  subscribeToAssistantId(): Observable<string> {
    return this.assistantId$;
  }

  /**
   * 檢查 URL 中是否包含有效的 assistantid
   * @returns boolean
   */
  hasValidAssistantId(): boolean {
    const assistantId = this.getCurrentAssistantId();
    return assistantId.length > 0 && assistantId !== 'Unknown User';
  }

  /**
   * 取得完整的 URL 參數物件
   * @returns 包含所有 URL 參數的物件
   */
  getAllUrlParams(): { [key: string]: string } {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const params: { [key: string]: string } = {};
      
      urlParams.forEach((value, key) => {
        params[key] = value;
      });
      
      return params;
    } catch (error) {
      console.error('取得 URL 參數時發生錯誤:', error);
      return {};
    }
  }
}
